import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Eye, X } from "lucide-react";
import { Link } from "react-router-dom";

const statusBadge = (s) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    submitted: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${
        map[s] || map.submitted
      }`}
    >
      {s || "submitted"}
    </span>
  );
};

export default function RecruiterReferrals({ apps }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () =>
      (apps || []).filter((a) => {
        if (!q) return true;
        return [a.full_name, a.email, a.program, a.nationality]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q.toLowerCase()));
      }),
    [apps, q]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search referrals..."
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <Link
          to="/recruiter/dashboard/new"
          className="bg-cct-blue text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#1a3380]"
        >
          + New Referral
        </Link>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400 text-sm">
          No referrals to show
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 text-left text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Student</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Program</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, idx) => (
                  <motion.tr
                    key={a.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.025 }}
                    className="border-t border-slate-100 hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-semibold text-xs">
                          {a.full_name?.slice(0, 1)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            {a.full_name}
                          </div>
                          <div className="text-xs text-slate-400">
                            {a.nationality}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.email}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {a.program || "—"}
                    </td>
                    <td className="px-4 py-3">{statusBadge(a.status)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelected(a)}
                        className="inline-flex items-center gap-1 text-cct-blue text-xs font-semibold hover:underline"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ x: 400 }}
            animate={{ x: 0 }}
            transition={{ type: "spring", damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
          >
            <div className="p-6 border-b border-slate-200 flex justify-between items-start sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">
                  Referral
                </div>
                <h3 className="font-display text-2xl font-semibold text-cct-blue mt-1">
                  {selected.full_name}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-2 text-sm">
              {Object.entries(selected)
                .filter(
                  ([k]) =>
                    !(["_id", "id", "documents"].includes(k))
                )
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100"
                  >
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="col-span-2 text-slate-800 break-words">
                      {String(v ?? "—")}
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
